import {
  ClickAwayListener,
  IconButton,
  Input,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  alpha,
  styled,
  useTheme,
} from "@mui/material";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import ListItemText, { listItemTextClasses } from "@mui/material/ListItemText";
import { FileIcon as FilePhosphorIcon } from "@phosphor-icons/react/dist/csr/File";
import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";

import { DotsThreeVerticalIcon } from "@phosphor-icons/react/dist/csr/DotsThreeVertical";
import { FilePlusIcon } from "@phosphor-icons/react/dist/csr/FilePlus";
import { FlaskIcon } from "@phosphor-icons/react/dist/csr/Flask";
import { PencilIcon } from "@phosphor-icons/react/dist/csr/Pencil";
import { TrashIcon } from "@phosphor-icons/react/dist/csr/Trash";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DraftStatus from "../../constants/draftStatus";
import Permissions from "../../constants/permissions";
import DiffIcon from "../../icons/Diff.svg?react";
import { pluralize } from "../../lib/Util";
import { useEditorStoreContext } from "../../store/editor/EditorStoreProvider";
import {
  EditorTabVariant,
  selectErrorsAndWarnings,
  selectTestableExportDeclsForFile,
} from "../../store/editor/editorStore";
import type { File } from "../../store/editor/types";
import { IfPermission } from "../Permissions";

const FilesHeader = styled(Stack)(({ theme }) => ({
  position: "sticky",
  top: 0,
  backgroundColor: theme.palette.surfaceLight10.main,
  zIndex: 1,
}));

const FileListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== "deleted" && prop !== "menuOpen",
})<{ deleted: boolean; menuOpen: boolean }>(({ deleted, theme, menuOpen }) => ({
  [`& .file-options-icon`]: {
    visibility: "hidden",
  },
  "&:hover": {
    [`& .file-options-icon`]: {
      visibility: "visible",
    },
  },
  ...(menuOpen && {
    [`& .file-options-icon`]: {
      visibility: "visible",
    },
  }),
  ...(deleted && {
    [`& .${listItemTextClasses.root}`]: {
      textDecoration: "line-through",
      color: theme.palette.text.disabled,
    },
  }),
}));

const FileItemIcon = styled(ListItemIcon)({
  position: "relative",
  alignItems: "center",
  "& .compilation-info-icon": {
    position: "absolute",
    bottom: 2,
    right: 21,
  },
});

const FileIcon = ({ fileName }: { fileName: string }) => {
  const theme = useTheme();
  const [errors, warnings, beta] = useEditorStoreContext(selectErrorsAndWarnings(fileName));
  let iconColor: string | undefined = undefined;
  let compilationInfoIcon: React.ReactNode | null = null;
  if (warnings || beta) {
    iconColor = theme.palette.warning.main;
    compilationInfoIcon = (
      <WarningIcon
        size={11}
        color={theme.palette.warning.main}
        className="compilation-info-icon"
        data-testid="warning-icon"
      />
    );
  }
  if (errors) {
    iconColor = theme.palette.error.main;
    compilationInfoIcon = (
      <WarningIcon
        size={11}
        color={theme.palette.error.main}
        className="compilation-info-icon"
        data-testid="error-icon"
      />
    );
  }
  return (
    <FileItemIcon>
      <FilePhosphorIcon size={25} fontSize="large" className="list-item-icon" color={iconColor} />
      {compilationInfoIcon}
    </FileItemIcon>
  );
};

const ErrorMessage = styled("div")(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: "0.8em",
}));

const FileMenu = styled(Menu)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 120,
    boxShadow: theme.mixins.shadowSurfaceLight10,
    ...theme.applyStyles("dark", {
      boxShadow: theme.mixins.shadowSurfaceDark10,
    }),
    "& .MuiMenu-list": {
      padding: "4px 0",
    },
    "& .MuiMenuItem-root": {
      "& .MuiSvgIcon-root": {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      "&:active": {
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
      },
    },
  },
}));

const EditFile: React.FC<{
  file: string;
  setEditMode: (mode: boolean) => void;
}> = ({ file, setEditMode }) => {
  const [value, setValue] = useState(file);
  const [dollarError, setDollarError] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);
  const files = useEditorStoreContext((state) => state.files);
  const addFile = useEditorStoreContext((state) => state.addFile);
  const renameFile = useEditorStoreContext((state) => state.renameFile);
  const setAddNewFile = useEditorStoreContext((state) => state.setAddNewFile);

  const [searchParams, setSearchParams] = useSearchParams();

  const duplicateError =
    files.filter((f) => f.path === value && value !== file).length > 0 ? `A file named ${value} already exists.` : "";
  const error = dollarError || duplicateError;

  const onBlur = useCallback(() => {
    setEditMode(false);
    setAddNewFile(false);
  }, [setEditMode, setAddNewFile]);

  useEffect(() => {
    const inputNode = inputRef?.current;
    if (inputNode) inputNode.focus();
    return () => {
      inputNode?.blur();
    };
  }, []);

  const changeFileName = useCallback(() => {
    if (!value || error) return;

    if (file === "") {
      addFile(value);
      setAddNewFile(false);
      const currentParams = Object.fromEntries([...searchParams]);
      setSearchParams({
        ...currentParams,
        file: encodeURIComponent(value),
      });
    } else {
      renameFile(file, value);
      setEditMode(false);
    }
  }, [value, error, file, addFile, setAddNewFile, searchParams, setSearchParams, renameFile, setEditMode]);

  useEffect(() => {
    const inputNode = inputRef?.current;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") changeFileName();
      else if (e.key === "Escape") onBlur();
    };

    if (inputNode) {
      inputNode.addEventListener("keydown", handleKeyDown);

      return () => inputNode.removeEventListener("keydown", handleKeyDown);
    }
  }, [inputRef, changeFileName, onBlur]);

  return (
    <Stack direction="column" spacing={1}>
      <Input
        data-cy="new-file-input"
        name="new-file-input"
        inputRef={inputRef}
        value={value}
        onChange={(e) => {
          if (e.target.value[0] === "$") {
            setDollarError("File name cannot start with $");
            return;
          }
          setDollarError("");
          setValue(e.target.value);
        }}
      />
      {error ? <ErrorMessage data-cy="file-error-message">{error}</ErrorMessage> : null}
    </Stack>
  );
};

const TestIcon = ({ fileName }: { fileName: string }) => {
  const declarations = useEditorStoreContext(selectTestableExportDeclsForFile(fileName));

  if (!declarations) return null;

  const msg = `${declarations.length} Test ${pluralize("export", declarations.length)} [${declarations.join(",")}].`;
  return (
    <Tooltip id="tooltip" title={msg} placement="right" arrow>
      <FlaskIcon />
    </Tooltip>
  );
};

const FileItem = ({ file }: { file?: File }) => {
  const [fileItemState, setFileItemState] = useState<{
    editMode: boolean;
    menuAnchorEl: HTMLDivElement | null;
  }>({
    editMode: !file,
    menuAnchorEl: null,
  });

  const currentOpenFile = useEditorStoreContext((state) => state.currentOpenFile);
  const selectedFile = currentOpenFile?.path;
  const readOnly = useEditorStoreContext((state) => [DraftStatus.RUNNING, DraftStatus.REPLACED].includes(state.status));
  const fileItemRef = useRef<HTMLDivElement>(null);
  const openFileTab = useEditorStoreContext((state) => state.openFileTab);
  const deleteFile = useEditorStoreContext((state) => state.deleteFile);
  const [searchParams, setSearchParams] = useSearchParams();

  const handleEdit = (e: React.MouseEvent<HTMLLIElement>) => {
    e.stopPropagation();
    setFileItemState({ menuAnchorEl: null, editMode: true });
  };

  const handleFileSelected = (variant: EditorTabVariant = EditorTabVariant.Editor) => {
    if (!file || fileItemState.editMode) return;
    openFileTab(file.path, "file", file.editFlags?.originalPath || file.path, variant);
    const currentParams = Object.fromEntries([...searchParams]);
    setSearchParams({
      ...currentParams,
      variant,
      file: encodeURIComponent(file.path),
    });
    setFileItemState((state) => ({ ...state, menuAnchorEl: null }));
  };

  const handleDeleteFile = (e: React.MouseEvent<HTMLLIElement>) => {
    e.stopPropagation();
    if (file) {
      deleteFile(file.path);
    }
  };

  const onClickAway = () => {
    setFileItemState((state) => ({ ...state, editMode: false }));
  };

  useEffect(() => {
    const fileItem = fileItemRef?.current;
    if (selectedFile === file?.path && fileItem) {
      fileItem.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [selectedFile, file]);

  const [errors, warnings] = useEditorStoreContext(selectErrorsAndWarnings(file?.path || ""));

  const fileText = file ? file.path : "";
  let fileTextColor = "";
  if (warnings) {
    fileTextColor = "warning.main";
  }
  if (errors) {
    fileTextColor = "error.main";
  }

  return (
    <>
      <ClickAwayListener onClickAway={onClickAway}>
        <ListItem disablePadding>
          <FileListItemButton
            ref={fileItemRef}
            onClick={() => handleFileSelected()}
            data-selected={selectedFile === file?.path}
            selected={selectedFile === file?.path}
            data-testid={`file-item-${file?.path}`}
            deleted={file?.editFlags?.deleted ?? false}
            disabled={file?.editFlags?.deleted ?? false}
            disableRipple
            menuOpen={Boolean(fileItemState.menuAnchorEl)}
          >
            <FileIcon fileName={file?.path || ""} />
            {fileItemState.editMode ? (
              <EditFile
                file={file ? file.path : ""}
                setEditMode={(mode: boolean) => setFileItemState((state) => ({ ...state, editMode: mode }))}
              />
            ) : (
              <>
                <ListItemText
                  primary={fileText}
                  slotProps={{
                    primary: {
                      color: fileTextColor,
                    },
                  }}
                  data-testid="list-item-text"
                />
                <IfPermission permission={Permissions.DevelopScripts}>
                  {!readOnly && (
                    <ListItemSecondaryAction>
                      <Stack direction="row" sx={{ alignItems: "center" }}>
                        <TestIcon fileName={file?.path || ""} />
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            setFileItemState((state) => ({
                              ...state,
                              menuAnchorEl: fileItemRef.current,
                            }));
                          }}
                          data-testid="file-item-menu-btn"
                          className="file-options-icon"
                        >
                          <DotsThreeVerticalIcon />
                        </IconButton>
                      </Stack>
                    </ListItemSecondaryAction>
                  )}
                </IfPermission>
              </>
            )}
          </FileListItemButton>
        </ListItem>
      </ClickAwayListener>
      <FileMenu
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "center",
          horizontal: "left",
        }}
        open={Boolean(fileItemState.menuAnchorEl)}
        anchorEl={fileItemState.menuAnchorEl}
        onClose={() => setFileItemState((state) => ({ ...state, menuAnchorEl: null }))}
      >
        <MenuItem sx={{ gap: "12px" }} onClick={handleEdit} disabled={file?.editFlags?.deleted}>
          <PencilIcon />
          Edit
        </MenuItem>
        <MenuItem sx={{ gap: "12px" }} onClick={handleDeleteFile} disabled={file?.editFlags?.deleted}>
          <TrashIcon />
          Delete
        </MenuItem>
        <MenuItem sx={{ gap: "12px" }} onClick={() => handleFileSelected(EditorTabVariant.EditableDiff)}>
          <DiffIcon />
          Compare Changes
        </MenuItem>
      </FileMenu>
    </>
  );
};

const FilePanel = () => {
  const readOnly = useEditorStoreContext((state) => [DraftStatus.RUNNING, DraftStatus.REPLACED].includes(state.status));

  const files = useEditorStoreContext((state) => state.files);

  const [addNewFile, setAddNewFile] = useEditorStoreContext((state) => [state.addNewFile, state.setAddNewFile]);

  return (
    <List disablePadding>
      <React.Fragment key="file-header">
        <FilesHeader
          direction="row"
          sx={{ justifyContent: "space-between", alignItems: "center", paddingLeft: 2, paddingRight: 2 }}
          key="file-header"
        >
          <Typography sx={{ fontSize: "1.5rem" }}>Files</Typography>
          {readOnly ? null : (
            <IconButton onClick={() => setAddNewFile(true)} data-testid="add-file-btn">
              <FilePlusIcon fontSize="large" />
            </IconButton>
          )}
        </FilesHeader>
      </React.Fragment>

      {files.map((file, idx) => (
        <React.Fragment key={idx}>
          <FileItem file={file} />
        </React.Fragment>
      ))}
      {addNewFile ? (
        <React.Fragment key="new-file">
          <FileItem />
        </React.Fragment>
      ) : null}
    </List>
  );
};

export default FilePanel;
